import { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import { getLiveSignal, type LiveSignalData } from '@/services/indexService';

const FALLBACK: LiveSignalData = {
  active_audits: 0,
  queued: 0,
  avg_latency_ms: 42,
  last_event: null,
};

type LiveSignalPanelProps = {
  /** Poll when authenticated (workspace). Static demo on marketing. */
  poll?: boolean;
  className?: string;
};

export default function LiveSignalPanel({ poll = false, className }: LiveSignalPanelProps) {
  const [data, setData] = useState<LiveSignalData>(FALLBACK);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!poll) {
      setData({ ...FALLBACK, active_audits: 3, queued: 7, last_event: 'SCR-00481 CLEARED · 84.7' });
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const signal = await getLiveSignal();
        if (!cancelled) {
          setData(signal);
          setLive(true);
        }
      } catch {
        if (!cancelled) setLive(false);
      }
    };
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [poll]);

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-4 w-4 text-index-accent" />
        <p className="ni-label">Live signal</p>
        <span className="text-[9px] font-mono text-white/25 ml-auto">
          {poll ? (live ? 'LIVE' : 'STATIC') : 'DEMO'}
        </span>
      </div>
      <div className="ni-panel p-4 font-mono text-xs space-y-3">
        <div className="flex justify-between text-white/55">
          <span>Active audit runs</span>
          <span className="text-index-cleared tabular-nums">{data.active_audits}</span>
        </div>
        <div className="flex justify-between text-white/55">
          <span>Queue depth</span>
          <span className="text-index-rewrite tabular-nums">{data.queued}</span>
        </div>
        <div className="flex justify-between text-white/55">
          <span>API latency</span>
          <span className="tabular-nums">{data.avg_latency_ms}ms</span>
        </div>
        {data.last_event && (
          <div className="pt-2 border-t border-[#2a2a2a] text-white/40 truncate">
            Last: {data.last_event}
          </div>
        )}
      </div>
    </div>
  );
}
